<script>
  import { getContext, onMount } from 'svelte';
  import { format, init } from 'svelte-i18n';
  import { Link, link } from 'svelte-navigator';
  import {
    Breadcrumb,
    BreadcrumbItem,
    Button,
    Col,
    Container,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Row
  } from '@sveltestrap/sveltestrap';
  import { unparse } from 'papaparse';

  import { BulkActionModal } from '@client/components/bulk-action-modal';
  import { EmptyState } from '@client/components/empty-state';
  import { FilterBar } from '@client/components/filter-bar';
  import { SearchFilter } from '@client/components/filters/search';
  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { NoMatches } from '@client/components/no-matches';
  import { getNotificationsContext } from '@client/components/notifications';
  import { SelectCell, SelectHeaderCell, SelectionControls } from '@client/components/selection-controls';
  import { Spinner } from '@client/components/spinner';
  import { Table } from '@client/components/table';

  import { TargetingService } from '@client/services/targeting';

  import { appStore } from '@client/stores/app';
  import { userStore } from '@client/stores/user';

  import { downloadFile } from '@client/utils/download';
  import { createSelection, selectedItems } from '@client/utils/selection';

  import { deepCopyArray } from '@common/utils/arrays';

  import { ActionsCell, CampaignsCell, NameCell, TagsCell } from './cell-formatters';
  import { EditModal, ImportModal } from './modals';

  const { basepath } = getContext('config');
  const { addNotification } = getNotificationsContext();

  export let isDomains = false;
  export let data = [];
  export let campaigns = [];
  export let tags = [];
  export let onRefresh = () => {};

  let original = deepCopyArray(data);
  let filtered = deepCopyArray(original);
  let initialized = false;

  let exporting = false;
  let loading = false;
  let rows = data || [];
  let sortBy = 'name';
  let sortDirection = 1;

  let selected = {};
  let showEdit = false;
  let showFilters = true;
  let targeting = {};
  let search = '';

  let type = isDomains ? $format('label.DOMAINS') : $format('label.RECIPIENTS');
  let actionType = (isDomains ? $format('label.DOMAIN') : $format('label.RECIPIENT')).toLowerCase();

  const getAccountKey = ({ name }) => name;
  const selection = createSelection(getAccountKey);

  let selecting = false;
  let showBulkDelete = false;
  let bulkItems = [];

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const onAdd = () => {
    selected = {
      new: true,
      name: '',
      campaigns: [],
      tags: []
    };

    showEdit = true;
  };

  const onSave = async (entry, removeEntry) => {
    let accounts = entry?.new ? [...targeting.accounts, entry] : targeting.accounts;

    if (removeEntry?.name) {
      accounts = accounts.filter((account) => account.name !== removeEntry.name);
    }

    loading = true;

    const payload = {
      accounts: accounts.map((account) => {
        if (account.new) {
          delete account.new;
        }

        if (account.name === entry?.name) {
          return {
            name: entry.name,
            tags: (entry.tags || []).map(({ value }) => ({ text: value })),
            ads: (entry.campaigns || []).map(({ value }) => ({ text: value }))
          };
        }

        return account;
      }),
      departments: targeting.departments
    };

    const response = await TargetingService.update(payload);

    if (response?.status >= 400) {
      notify($format('label.FAILURE.TARGETING_UPDATE'), 'error');
    } else {
      notify($format('label.SUCCESS.TARGETING_UPDATED'));
    }

    onRefresh();

    targeting = await TargetingService.get();

    loading = false;
  };

  const onDelete = async (item) => {
    const confirmed = confirm(
      $format('label.DELETE_PROMPT', {
        values: {
          type: actionType
        }
      })
    );

    if (confirmed) {
      await onSave({ name: item.name, ads: [], tags: [] });
    }
  };

  const actionsConfig = {
    props: {
      onEdit: (account) => {
        selected = account;
        showEdit = true;
      },
      onDelete
    }
  };

  const onOpenBulkDelete = () => {
    bulkItems = selectedAccounts;
    showBulkDelete = true;
  };

  // Targeting is one document, so the batch is a single save without the chosen accounts.
  const runBulkDelete = async (items) => {
    const names = new Set(items.map(getAccountKey));
    const response = await TargetingService.update({
      accounts: targeting.accounts.filter((account) => !names.has(account.name)),
      departments: targeting.departments,
      matchmode: targeting.matchmode
    });

    return !(response?.status >= 400);
  };

  const onBulkDeleteFinish = async (failed) => {
    const deleted = bulkItems.length - failed.length;

    if (deleted) {
      notify(
        $format(isDomains ? 'label.BULK_ACTION.NOTIFY_DELETE_DOMAINS' : 'label.BULK_ACTION.NOTIFY_DELETE_RECIPIENTS', {
          values: { count: deleted }
        })
      );
    }

    // Leave only the failures selected so they can be retried directly.
    if (failed.length) {
      selection.selectAll(failed);
    } else {
      selecting = false;
      selection.clear();
    }

    loading = true;

    await onRefresh();

    targeting = await TargetingService.get();

    loading = false;
  };

  const onClickRow = ({ detail }) => {
    if (!selecting || detail.event.target.closest('input, button, a')) {
      return;
    }

    selection.toggle(detail.row);
  };

  const onClickCard = (event, row) => {
    if (!selecting || event.target.closest('input, button, a')) {
      return;
    }

    selection.toggle(row);
  };

  let columns = [
    {
      key: 'name',
      title: isDomains ? $format('label.DOMAIN') : $format('label.EMAIL'),
      value: ({ name }) => name,
      renderComponent: NameCell,
      sortable: true
    },
    {
      key: 'ads',
      title: $format('label.CAMPAIGNS'),
      value: (row) => row.campaigns.map(({ label }) => label).join() || '-',
      headerClass: 'campaigns-cell',
      renderComponent: CampaignsCell,
      sortable: true
    },
    {
      key: 'tags',
      title: $format('label.TAGS'),
      value: (row) => row.tags.map(({ label }) => label).join() || '-',
      headerClass: 'tags-cell',
      renderComponent: TagsCell,
      sortable: true
    },
    {
      key: 'actions',
      title: '',
      value: '',
      headerClass: 'actions-cell',
      renderComponent: {
        component: ActionsCell,
        props: actionsConfig.props
      }
    }
  ];

  const onSearch = () => {
    let rows = [...original];

    if (search.length) {
      rows = rows.filter((row) => {
        const searchValue = search.toLowerCase();

        return Object.values(row).some((field) => {
          if (typeof field === 'string') {
            return field.toLowerCase().includes(searchValue);
          }

          if (field instanceof Array) {
            return field.filter(({ text, value }) => (text || value)?.toLowerCase().includes(searchValue)).length;
          }

          return false;
        });
      });
    }

    filtered = [...rows];
  };

  const onExport = async (exportAll) => {
    exporting = true;

    const results = exportAll ? original : filtered;
    const size = results.length;
    const data = results.map(({ name, campaigns, tags }) => ({
      [actionType]: name,
      campaigns: campaigns.map(({ value }) => value).join(),
      tags: tags.map(({ value }) => value).join()
    }));

    const text = unparse(data);
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
    const today = new Date().getTime();

    downloadFile(blob, `${type}-targeting-${$userStore.domain}-${today}.csv`);

    exporting = false;
  };

  let showImportModal = false;

  const onImport = () => {
    showImportModal = true;
  };

  const onSaveImport = async ({ data, errors }) => {
    const entries = data.map((item) => ({
      name: item['email/domain'],
      ads: item.campaigns?.length ? item.campaigns.split(',') : [],
      tags: item.tags?.length ? item.tags.split(',') : []
    }));

    const updatedAccounts = [...targeting.accounts];

    entries.forEach((target) => {
      const existingAccount = updatedAccounts.find((account) => account.name === target.name);

      if (existingAccount) {
        if (target.ads?.length) {
          target.ads.forEach((campaign) => {
            const existingAd = existingAccount.ads.find(({ text }) => text === campaign);

            if (!existingAd) {
              existingAccount.ads.push({
                text: campaign
              });
            }
          });
        }

        if (target.tags?.length) {
          target.tags.forEach((tag) => {
            const existingAd = existingAccount.tags.find(({ text }) => text === tag);

            if (!existingAd) {
              existingAccount.tags.push({
                text: tag
              });
            }
          });
        }
      } else {
        if (target?.name) {
          updatedAccounts.push({
            name: target.name,
            ads: target.ads?.map((text) => ({ text })) || [],
            tags: target.tags?.map((text) => ({ text })) || []
          });
        }
      }
    });

    loading = true;

    const response = await TargetingService.update({
      accounts: updatedAccounts,
      departments: targeting.departments
    });

    showImportModal = false;

    if (response?.status >= 400) {
      notify($format('label.FAILURE.TARGETING_UPDATE'), 'error');
    } else {
      notify($format('label.SUCCESS.TARGETING_UPDATED'));
    }

    onRefresh();

    targeting = await TargetingService.get();

    loading = false;
  };

  onMount(async () => {
    loading = true;
    targeting = await TargetingService.get();
    initialized = true;
    loading = false;
  });

  $: onSearch(search);

  $: if (data) {
    original = [...deepCopyArray(data)];
    filtered = [...deepCopyArray(original)];
  }
  $: hasTargeting = initialized && original.length > 0;
  $: showCards = $appStore.tablet || $appStore.mobile;
  $: selectColumn = {
    key: 'select',
    title: { component: SelectHeaderCell, props: { selection, rows: filtered } },
    value: () => '',
    renderComponent: { component: SelectCell, props: { selection, getKey: getAccountKey } },
    headerClass: 'select-cell'
  };
  $: tableColumns = selecting ? [selectColumn, ...columns.filter((column) => column.key !== 'actions')] : columns;
  $: selectedAccounts = selectedItems(original, $selection, getAccountKey);
  // Keeps the count honest when a search or refresh drops selected accounts.
  $: selecting && selection.prune(filtered);
</script>

<div id="targeting-accounts" class="page">
  <Loader backdrop show={loading} />
  <div class="page-header no-bottom-border">
    <Container fluid>
      <Row>
        <Col>
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/marketing/campaigns" use:link>{$format('label.MARKETING')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <a href="{basepath}/marketing/targeting" use:link>{$format('label.TARGETING')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{type}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row style="max-width: {hasTargeting ? '1268px' : '100%'};">
        <Col xs="4" sm="4" md="8">
          <h1 class="page-title pt-1">
            {type}
          </h1>
        </Col>
        <Col class="text-right">
          {#if initialized}
            <Button color="secondary" class="me-2" on:click={onImport}>
              {$format('label.IMPORT')}
            </Button>
            <Button color="secondary" on:click={onAdd}
              >{isDomains ? $format('label.ADD_DOMAIN') : $format('label.ADD_RECIPIENT')}</Button
            >
          {/if}
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    <div class="tabs" class:fill={!hasTargeting}>
      <div class="tab-list">
        <Link class="tab" to="/marketing/targeting/groups">{$format('label.GROUPS')}</Link>
        <Link class="tab {isDomains ? 'active' : ''}" to="/marketing/targeting/domains">{$format('label.DOMAINS')}</Link
        >
        <Link class="tab {!isDomains ? 'active' : ''}" to="/marketing/targeting/recipients"
          >{$format('label.RECIPIENTS')}</Link
        >
        <Link class="tab" to="/marketing/targeting/diagnostics">{$format('label.PREVIEW')}</Link>
      </div>
    </div>
    {#if hasTargeting}
      <div class="toolbar" class:collapsed={!showFilters}>
        <Container fluid>
          <Row>
            <Col lg="6" md="6" sm="12">
              <div class="toolbar-left" class:collapsed={!showFilters}>
                <FilterBar bind:show={showFilters}>
                  <SearchFilter bind:search />
                </FilterBar>
              </div>
            </Col>
            <Col lg="6" md="6" sm="12">
              <div class="toolbar-right">
                <!-- Hidden while selecting: the selected count is its own "n of n" and two together read as a conflict. -->
                {#if !selecting}
                  {#if filtered.length !== original.length}
                    <small class="count">
                      {$format('label.MATCHES_X_OF_X_TYPE', {
                        values: {
                          filtered: filtered?.length.toLocaleString(),
                          total: original?.length.toLocaleString(),
                          type
                        }
                      })}
                    </small>
                  {:else}
                    <small class="count">
                      {$format('label.TOTAL_X_TYPE', {
                        values: {
                          total: original?.length.toLocaleString(),
                          type
                        }
                      })}
                    </small>
                  {/if}
                {/if}
                <SelectionControls
                  bind:selecting
                  {selection}
                  rows={filtered}
                  id={isDomains ? 'domains' : 'recipients'}
                  onDelete={onOpenBulkDelete}
                />
                <Dropdown id="export-dropdown">
                  <DropdownToggle size="sm" class="export-dropdown" disabled={!filtered.length || exporting}>
                    {#if exporting}
                      <Spinner styles="border-width: 2px; color: #0087ff; height: 14px; width: 14px;" show inline />
                    {:else}
                      <Icon name="download" />
                      <span class="visually-hidden">{$format('label.EXPORT')}</span>
                    {/if}
                  </DropdownToggle>
                  <DropdownMenu end>
                    {#if filtered.length !== original.length}
                      <DropdownItem on:click={() => onExport()}>
                        {@html $format('label.EXPORT_X_TYPE', {
                          values: {
                            total: filtered?.length.toLocaleString(),
                            type
                          }
                        })}
                      </DropdownItem>
                      <DropdownItem divider />
                    {/if}
                    <DropdownItem on:click={() => onExport(true)}>
                      {@html $format('label.EXPORT_ALL_TYPE', {
                        values: {
                          type
                        }
                      })}
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <div class="accounts-layout">
        {#if filtered.length}
          {#if showCards}
            {#each filtered as row}
              {@const isSelected = $selection.has(row.name)}
              <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
              <div
                class="account-card"
                class:selecting
                class:border-primary={isSelected}
                class:bg-primary-subtle={isSelected}
                on:click={(event) => onClickCard(event, row)}
              >
                {#if selecting}
                  <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
                  <div class="select" on:click|stopPropagation>
                    <input
                      class="form-check-input"
                      type="checkbox"
                      checked={isSelected}
                      on:change={() => selection.toggle(row)}
                      aria-label="{$format('label.SELECT')} {row.name}"
                    />
                  </div>
                {/if}
                <div class="account-card-header">
                  <NameCell {row} />
                </div>
                <div class="account-card-section">
                  <b>{$format('label.CAMPAIGNS')}</b>
                  <CampaignsCell {row} />
                </div>
                <div class="account-card-section">
                  <b>{$format('label.TAGS')}</b>
                  <TagsCell {row} />
                </div>
                {#if !selecting}
                  <div class="account-card-actions">
                    <ActionsCell {row} {...actionsConfig} />
                  </div>
                {/if}
              </div>
            {/each}
          {:else}
            <Table
              id="accounts"
              classNameThead="sticky-header {!showFilters ? 'collapsed' : ''}"
              emptyLabel={$format('label.MESSAGE.NO_MATCHES_TARGETING')}
              bind:sortBy
              bind:sortOrder={sortDirection}
              columns={tableColumns}
              bind:rows={filtered}
              rowClass={(row) => ($selection.has(row.name) ? 'bg-primary-subtle' : '')}
              on:clickRow={onClickRow}
            />
          {/if}
        {:else if initialized}
          <NoMatches message={$format('label.MESSAGE.NO_MATCHES_TARGETING')} />
        {/if}
      </div>
    {:else if initialized}
      {#if isDomains}
        <EmptyState
          icon="globe"
          heading={$format('label.EMPTY_STATE.TARGETING_DOMAINS_HEADING')}
          description={$format('label.EMPTY_STATE.TARGETING_DOMAINS_DESCRIPTION')}
          action={$format('label.EMPTY_STATE.TARGETING_DOMAINS_ACTION')}
          on:click={onAdd}
        />
      {:else}
        <EmptyState
          icon="user"
          heading={$format('label.EMPTY_STATE.TARGETING_RECIPIENTS_HEADING')}
          description={$format('label.EMPTY_STATE.TARGETING_RECIPIENTS_DESCRIPTION')}
          action={$format('label.EMPTY_STATE.TARGETING_RECIPIENTS_ACTION')}
          on:click={onAdd}
        />
      {/if}
    {/if}
  </div>
</div>

{#if showEdit}
  <EditModal bind:show={showEdit} onUpdate={onSave} data={selected} {isDomains} {campaigns} {tags} {targeting} />
{/if}

{#if showImportModal}
  <ImportModal bind:show={showImportModal} onUpdate={onSaveImport} />
{/if}

{#if showBulkDelete}
  <BulkActionModal
    bind:show={showBulkDelete}
    items={bulkItems}
    title={$format(
      isDomains ? 'label.BULK_ACTION.CONFIRM_DELETE_DOMAINS' : 'label.BULK_ACTION.CONFIRM_DELETE_RECIPIENTS',
      {
        values: { count: bulkItems.length }
      }
    )}
    warning={$format('label.BULK_DELETE.WARNING')}
    confirmLabel={$format('label.DELETE')}
    confirmColor="danger"
    progressLabel={$format('label.BULK_ACTION.PROGRESS_TARGETING')}
    leaveMessage={$format('label.BULK_ACTION.CONFIRM_LEAVE_TARGETING')}
    getName={getAccountKey}
    onRun={runBulkDelete}
    onFinish={onBulkDeleteFinish}
    batch
  />
{/if}

<style lang="scss" src="./accounts.scss"></style>
